import {
  SEARCH_QUERY_MAX,
  SEARCH_TERM_MIN,
  findTermSpans,
  parseSearchQuery,
} from '@matter_extractor/shared';
import type { SearchSpan, SessionSearchHit } from '@matter_extractor/shared';

/**
 * The words of the search in Sessions (`recherche-sessions.md` §4 to §7) - PURE,
 * so the rules the screen plays are tested without a DOM.
 *
 * The matching itself is NOT here: `parseSearchQuery` and `findTermSpans` of
 * `shared` are the ONE implementation the server plays to retain a session and
 * the screen plays to underline what it found. This module only decides what the
 * address and the field say about the text an operator typed.
 */

/** The delay between the last keystroke and the read of the list (§4.3, the design system's). */
export const SEARCH_DELAY_MS = 200;

/** The sentence under the field when the text typed does not arm a search (§4.4). */
export const SEARCH_MINIMUM_SENTENCE = 'La recherche commence à deux caractères.';

/**
 * The `?q=` of the address, as `validateSearch` reads it - and it NEVER throws.
 *
 * Trimmed, cut at 200 characters, and ABSENT when what is left is shorter than
 * two (§4.3). A number is a string the router parsed as JSON (`?q=42` typed by
 * hand), so it reads as its digits - the trap `readSearch` of the conversation
 * documents for `?tour=`.
 */
export function readSearchQuery(value: unknown): string | undefined {
  const raw =
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : '';
  const text = raw.slice(0, SEARCH_QUERY_MAX).trim();
  return text.length < SEARCH_TERM_MIN ? undefined : text;
}

/**
 * The search the LIST actually reads, or `undefined` (H6): the text of the address
 * only when `parseSearchQuery` finds a word in it. A `?q=a b` is in the address
 * and arms nothing - no word of two characters.
 */
export function armedSearch(q: string | undefined): string | undefined {
  return q !== undefined && parseSearchQuery(q) !== null ? q : undefined;
}

/** The folded words to underline, or `null` without an armed search. */
export function searchTermsOf(q: string | undefined): readonly string[] | null {
  const armed = armedSearch(q);
  return armed === undefined ? null : parseSearchQuery(armed);
}

/**
 * What typing `text` asks of the address (§4.3, §4.4, H6).
 *
 *   - `undefined`: the field is empty, the search leaves the address;
 *   - `null`: HOLD - there is text, but no word of two characters. Nothing moves:
 *     the list and the address keep the search they had (« un seul caractère :
 *     rien ne bouge ») and the screen says why;
 *   - a string: the search to write.
 */
export function queryOfTyped(text: string): string | null | undefined {
  if (text.trim() === '') return undefined;
  const read = readSearchQuery(text);
  return read !== undefined && parseSearchQuery(read) !== null ? read : null;
}

/** The phrase under the field: there is text, and it does not arm a search. */
export function belowMinimum(text: string): boolean {
  return queryOfTyped(text) === null;
}

/** « 3 sessions trouvées » for the polite region (§4.4). */
export function foundSentence(count: number): string {
  if (count === 0) return 'Aucune session trouvée';
  return count === 1 ? '1 session trouvée' : `${count} sessions trouvées`;
}

/** The title of the empty state (§6). */
export function noMatchTitle(query: string): string {
  return `Aucune session ne contient « ${query} »`;
}

/** « 1 filtre actif » / « 2 filtres actifs ». */
export function activeFiltersWords(count: number): string {
  return count === 1 ? '1 filtre actif' : `${count} filtres actifs`;
}

/** The description of the empty state: the rule, then the filters when some are armed (§6). */
export function noMatchSentence(activeFilters: number): string {
  const rule =
    'Chaque mot doit se trouver dans la session. Vérifiez l’orthographe, ou retirez un mot.';
  return activeFilters > 0
    ? `${rule} La recherche ne porte que sur les sessions que les filtres gardent : ${activeFiltersWords(activeFilters)}.`
    : rule;
}

/** « + 2 autres endroits » - the button under the first place (§5). */
export function moreHitsLabel(others: number): string {
  return others === 1 ? '+ 1 autre endroit' : `+ ${others} autres endroits`;
}

/** The last line of an unfolded list when the places outnumber the twenty served (§5). */
export function hiddenHitsLabel(hidden: number): string {
  return hidden === 1 ? 'et 1 autre' : `et ${hidden} autres`;
}

/**
 * Where a place says it found the words, as the line spells it AFTER « trouvé dans ».
 *
 * A field is named by its label between « », a field of a version that no longer
 * reads is « un champ d’une page » (§10), the identifier is not displayed anywhere
 * else so it says what it is, and a reply is a LINK - its words are the link text,
 * `turn` being the 1-based number a human reads (`?tour=` of §12.3 is 1-based,
 * `turnIndex` of the contract 0-based).
 */
export type HitPlace =
  | { readonly kind: 'field'; readonly words: string }
  | { readonly kind: 'session-id'; readonly words: string }
  | { readonly kind: 'reply'; readonly words: string; readonly turn: number };

export function hitPlace(hit: SessionSearchHit): HitPlace {
  if (hit.kind === 'reply') {
    const turn = (hit.turnIndex ?? 0) + 1;
    return { kind: 'reply', words: `la réponse du tour ${turn}`, turn };
  }
  if (hit.kind === 'session-id') {
    return { kind: 'session-id', words: 'l’identifiant de la session' };
  }
  return {
    kind: 'field',
    words: hit.label === null ? 'un champ d’une page' : `« ${hit.label} »`,
  };
}

/** A text cut into runs, the ones inside a span being marked. */
export type TextRun = { readonly text: string; readonly marked: boolean };

/**
 * Cuts `text` at the `[start, end)` passages (sorted, as `findTermSpans` and the
 * excerpts of the contract hand them back).
 *
 * Defensive on spans it cannot trust: the ones the server sent are an external
 * input, so a span past the end is clipped and an inverted or overlapping one is
 * skipped rather than throwing in a render.
 */
export function textRuns(text: string, spans: readonly SearchSpan[]): TextRun[] {
  const runs: TextRun[] = [];
  let at = 0;
  for (const [rawStart, rawEnd] of spans) {
    const start = Math.max(at, Math.min(rawStart, text.length));
    const end = Math.min(rawEnd, text.length);
    if (end <= start) continue;
    if (start > at) runs.push({ text: text.slice(at, start), marked: false });
    runs.push({ text: text.slice(start, end), marked: true });
    at = end;
  }
  if (at < text.length) runs.push({ text: text.slice(at), marked: false });
  return runs;
}

/** The runs of a visible column: the passages of the folded `terms` marked. */
export function highlightRuns(text: string, terms: readonly string[] | null): TextRun[] {
  if (terms === null) return [{ text, marked: false }];
  return textRuns(text, findTermSpans(text, terms));
}

/**
 * The sentence above the objectives of Matière (§7), from the `search` of the
 * answer. The experience's sessions are « de cette expérience » whatever their
 * number; zero retained is the other sentence.
 */
export function matterSearchSentence(
  query: string,
  search: { readonly matchedSessions: number; readonly totalSessions: number },
): string {
  if (search.matchedSessions === 0) {
    return `Aucun extrait : aucune session de cette expérience ne contient « ${query} ».`;
  }
  const word = search.matchedSessions === 1 ? 'session' : 'sessions';
  return `Extraits de ${search.matchedSessions} ${word} sur ${search.totalSessions} de cette expérience : celles que la recherche « ${query} » retient.`;
}
